import { Fragment, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Engine } from '@rossum/api-client/engines';
import CopyToClipboardButton from '@rossum/components/copy-to-clipboard/CopyToClipboardButton';
import { DeleteConfirmationDialog } from '@rossum/components/delete-confirmation-dialog/DeleteConfirmationDialog';
import { Header, HeaderProps } from '@rossum/components/header/Header';
import {
  Button,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { AutomationBreadcrumbs } from '../../../containers/Automation/AutomationBreadcrumbs';
import { automationEnginesPath } from '../../../containers/Automation/helpers';
import {
  restrictedEngineCreationEnabledSelector,
  restrictedEngineEditEnabledSelector,
} from '../../../redux/modules/organizationGroup/selectors';
import { DuplicateEngineDialog } from '../components/engine-duplicate/DuplicateEngineDialog';
import { ENGINE_FORM_ID } from '../constants';
import { useDeleteEngine } from '../hooks/useDeleteEngine.ts';
import { useQueuesForEngine } from '../hooks/useQueuesForEngine';
import { basePath, engineBasicSettingsPath } from '../routes';
import { EngineSettingsTabs } from './EngineSettingsTabs';

type EngineBasicSettingsHeaderProps = {
  engine: Engine;
  isSaving: boolean;
} & Pick<HeaderProps, 'scrollableDivRef'>;

export const EngineBasicSettingsHeader = ({
  engine,
  isSaving,
  ...rest
}: EngineBasicSettingsHeaderProps) => {
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const isRestrictedEngineCreationEnabled = useSelector(
    restrictedEngineCreationEnabledSelector
  );

  const isRestrictedEngineEditEnabled = useSelector(
    restrictedEngineEditEnabledSelector
  );

  const intl = useIntl();
  const { mutate: deleteEngine, isLoading: isDeletingEngine } =
    useDeleteEngine();

  const { data } = useQueuesForEngine({
    engine: [engine.id],
  });

  const totalCount = data?.pagination?.total ?? 0;
  const deleteDisabled = totalCount !== undefined && totalCount > 0;

  const history = useHistory();

  return (
    <Header
      {...rest}
      breadcrumbs={
        <AutomationBreadcrumbs
          breadcrumbs={[
            {
              label: intl.formatMessage({
                id: 'components.appBar.menu.automation.aiEngines',
              }),
              to: automationEnginesPath(),
            },
            {
              label: engine.name,
              to: engineBasicSettingsPath(engine.id),
            },
            {
              label: intl.formatMessage({
                id: 'features.engines.header.tabs.settings.basic',
              }),
            },
          ]}
        />
      }
      tabs={<EngineSettingsTabs value="basic-settings" engineId={engine.id} />}
      title={engine.name}
      description={
        <Stack>
          <Typography variant="body2">{engine.description}</Typography>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="body2">
              {intl.formatMessage({
                id: 'features.engines.header.description.engineId',
              })}
              {': '}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {engine.agendaId}
            </Typography>
            <CopyToClipboardButton
              content={`${engine.agendaId}`}
              iconColor="secondary"
            />
          </Stack>
        </Stack>
      }
      buttons={
        isRestrictedEngineEditEnabled
          ? []
          : [
              <Fragment key="delete">
                <Tooltip
                  title={
                    deleteDisabled
                      ? intl.formatMessage({
                          id: 'features.engines.header.buttons.delete.disabledTooltip',
                        })
                      : ''
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      color="secondary"
                      disabled={deleteDisabled || isDeletingEngine}
                      data-cy="delete-button"
                      onClick={() => setDeleteDialogOpen(true)}
                      startIcon={
                        isDeletingEngine && (
                          <CircularProgress color="inherit" size={20} />
                        )
                      }
                    >
                      {intl.formatMessage({
                        id: 'features.engines.header.buttons.delete',
                      })}
                    </Button>
                  </span>
                </Tooltip>
                <DeleteConfirmationDialog
                  title={intl.formatMessage({
                    id: `features.engines.dialog.deleteEngine.title`,
                  })}
                  description={intl.formatMessage({
                    id: `features.engines.dialog.deleteEngine.text`,
                  })}
                  open={deleteDialogOpen}
                  onCancel={() => setDeleteDialogOpen(false)}
                  onConfirm={() => {
                    setDeleteDialogOpen(false);
                    deleteEngine(engine.id, {
                      onSuccess: () => history.push(basePath),
                    });
                  }}
                />
              </Fragment>,
              <Fragment key="duplicate">
                <Tooltip
                  title={
                    isRestrictedEngineCreationEnabled
                      ? intl.formatMessage({
                          id: 'features.engines.creation.disabled',
                        })
                      : ''
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      color="secondary"
                      disabled={isRestrictedEngineCreationEnabled}
                      onClick={() => setDuplicateDialogOpen(true)}
                      data-cy="duplicate-engine-button"
                    >
                      {intl.formatMessage({
                        id: 'features.engines.header.buttons.duplicate',
                      })}
                    </Button>
                  </span>
                </Tooltip>
                <DuplicateEngineDialog
                  engine={engine}
                  open={duplicateDialogOpen}
                  closeDialog={() => setDuplicateDialogOpen(false)}
                />
              </Fragment>,
              <Button
                key="save_engine"
                variant="contained"
                type="submit"
                form={ENGINE_FORM_ID}
                startIcon={
                  isSaving && <CircularProgress color="inherit" size={20} />
                }
                disabled={isSaving}
                data-cy="submit-form"
              >
                {intl.formatMessage({
                  id: 'features.engines.header.buttons.save',
                })}
              </Button>,
            ]
      }
    />
  );
};
